import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Content,
  Header,
  InfoCard,
  Page,
  Progress,
  ResponseErrorPanel,
  Table,
} from '@backstage/core-components';
import {
  discoveryApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';
import { Button, Grid, Typography } from '@material-ui/core';
import type {
  LifecycleAction,
  MonthlyCostObservation,
  QuotaReservation,
  ServiceAccessRequest,
  EntityScorecardResult,
  DriftObservation,
  DriftReconciliation,
  ServiceMetadataSnapshot,
  ProjectEntitlement,
} from '@cubeangle/arta-common';

interface VisibleServiceAccessRequest extends ServiceAccessRequest {
  canCancel?: boolean;
  canResolve?: boolean;
}

interface VisibleServiceMetadataSnapshot extends ServiceMetadataSnapshot {
  stale: boolean;
}

interface AccessDestination {
  title: string;
  url: string;
}

interface AccessRow {
  entityRef: string;
  name: string;
  kind: string;
  type: string;
  environment: string;
  cloud: string;
  owner: string;
  description: string;
  destinations: AccessDestination[];
  connection: string;
  accessInstructions: string;
  serviceStatus: string;
  metadataFreshness: string;
  actualMonthlyCost: string;
}

interface TemplateAccessItem {
  projectRef: string;
  templateRef: string;
  operation: string;
}

interface BoundaryValidationResult {
  cloud: 'AWS' | 'Azure';
  projectRef: string;
  templateRef: string;
  approvedTarget: string;
  approvedResult: string;
  escapedTarget: string;
  escapedResult: string;
  passed: boolean;
}

function normalizeRef(ref: string, kind = 'group'): string {
  const value = ref.trim().toLowerCase();
  if (!value.includes(':')) return `${kind}:default/${value}`;
  const [prefix, name] = value.split(':', 2);
  return name.includes('/') ? `${prefix}:${name}` : `${prefix}:default/${name}`;
}

function entityRef(entity: Entity): string {
  return `${entity.kind}:${entity.metadata.namespace ?? 'default'}/${
    entity.metadata.name
  }`.toLowerCase();
}

function catalogPath(ref: string): string {
  const [kind, qualifiedName = 'default/unknown'] = ref.split(':', 2);
  const [namespace, name] = qualifiedName.includes('/')
    ? qualifiedName.split('/', 2)
    : ['default', qualifiedName];
  return `/catalog/${namespace}/${kind}/${name}`;
}

function stringValue(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function destinations(entity: Entity): AccessDestination[] {
  const metadata = entity.metadata as Record<string, unknown>;
  const annotations = entity.metadata.annotations ?? {};
  const items: AccessDestination[] = [...(entity.metadata.links ?? [])]
    .filter(link => /^https?:\/\//i.test(link.url))
    .map(link => ({ title: link.title ?? 'Open service', url: link.url }));
  const workspaceUrl = stringValue(metadata.workspaceUrl);
  if (workspaceUrl && !items.some(item => item.url === workspaceUrl)) {
    items.unshift({ title: 'Open workspace', url: workspaceUrl });
  }
  const source = annotations['backstage.io/source-location'];
  if (source?.startsWith('url:https://')) {
    items.push({ title: 'Source repository', url: source.slice(4) });
  }
  const adoHost = annotations['dev.azure.com/host-org'];
  const adoRepo = annotations['dev.azure.com/project-repo'];
  if (adoHost && adoRepo) {
    items.push({
      title: 'Azure DevOps repository',
      url: `https://${adoHost.replace(/^https?:\/\//, '')}/${adoRepo.replace(
        '/',
        '/_git/',
      )}`,
    });
  }
  return items.filter(
    (item, index) => items.findIndex(other => other.url === item.url) === index,
  );
}

function toRow(entity: Entity): AccessRow {
  const metadata = entity.metadata as Record<string, unknown>;
  const spec = (entity.spec ?? {}) as Record<string, unknown>;
  const tags = entity.metadata.tags ?? [];
  const cloud = tags.includes('azure')
    ? 'Azure'
    : tags.includes('aws')
    ? 'AWS'
    : '';
  const connection = [
    stringValue(metadata.catalogName) &&
      `Catalog: ${stringValue(metadata.catalogName)}`,
    stringValue(metadata.resourceGroup) &&
      `Resource group: ${stringValue(metadata.resourceGroup)}`,
    stringValue(metadata.azureRegion) &&
      `Region: ${stringValue(metadata.azureRegion)}`,
    stringValue(metadata.awsRegion) &&
      `Region: ${stringValue(metadata.awsRegion)}`,
  ]
    .filter(Boolean)
    .join(' · ');
  return {
    entityRef: entityRef(entity),
    name: entity.metadata.title ?? entity.metadata.name,
    kind: entity.kind,
    type: stringValue(spec.type) || stringValue(spec.subType),
    environment:
      stringValue(metadata.envType) || stringValue(metadata.environment),
    cloud,
    owner: stringValue(spec.owner),
    description: entity.metadata.description ?? '',
    destinations: destinations(entity),
    connection,
    accessInstructions:
      entity.metadata.annotations?.['arta.cubeangle.io/access-instructions'] ??
      '',
    serviceStatus: '',
    metadataFreshness: '',
    actualMonthlyCost: '',
  };
}

function withSynchronizedMetadata(
  row: AccessRow,
  snapshot: VisibleServiceMetadataSnapshot | undefined,
): AccessRow {
  if (!snapshot) return row;
  const synchronizedDestinations = [
    ...(snapshot.workspaceUrl
      ? [{ title: 'Open workspace', url: snapshot.workspaceUrl }]
      : []),
    ...snapshot.destinations,
  ];
  const catalogDestinations = snapshot.workspaceUrl
    ? row.destinations.filter(item => item.title !== 'Open workspace')
    : row.destinations;
  const destinations = [
    ...synchronizedDestinations,
    ...catalogDestinations,
  ].filter(
    (item, index, items) =>
      items.findIndex(candidate => candidate.url === item.url) === index,
  );
  const connection = [
    snapshot.catalog && `Catalog: ${snapshot.catalog}`,
    snapshot.database && `Database: ${snapshot.database}`,
    snapshot.schema && `Schema: ${snapshot.schema}`,
    snapshot.resourceGroup && `Resource group: ${snapshot.resourceGroup}`,
    snapshot.region && `Region: ${snapshot.region}`,
  ]
    .filter(Boolean)
    .join(' · ');
  const instructions = [
    snapshot.authenticationInstructions,
    snapshot.networkInstructions,
    snapshot.maintenanceMessage,
  ]
    .filter(Boolean)
    .join(' · ');
  return {
    ...row,
    destinations,
    connection: connection || row.connection,
    accessInstructions: instructions || row.accessInstructions,
    serviceStatus: snapshot.status ?? 'unknown',
    metadataFreshness: snapshot.stale
      ? `Stale since ${new Date(
          snapshot.expiresAt ?? snapshot.updatedAt,
        ).toLocaleString()}`
      : `Observed ${new Date(snapshot.observedAt).toLocaleString()}`,
  };
}

function isOwned(entity: Entity, ownershipRefs: Set<string>): boolean {
  const owner = stringValue(entity.spec?.owner);
  if (owner && ownershipRefs.has(normalizeRef(owner))) return true;
  return Boolean(
    entity.relations?.some(
      relation =>
        relation.type === 'ownedBy' &&
        ownershipRefs.has(normalizeRef(relation.targetRef)),
    ),
  );
}

export const DeveloperAccessHubPage = () => {
  const catalogApi = useApi(catalogApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  const [entities, setEntities] = useState<Entity[]>();
  const [requests, setRequests] = useState<VisibleServiceAccessRequest[]>([]);
  const [serviceMetadata, setServiceMetadata] = useState<
    VisibleServiceMetadataSnapshot[]
  >([]);
  const [costObservations, setCostObservations] = useState<
    MonthlyCostObservation[]
  >([]);
  const [lifecycleActions, setLifecycleActions] = useState<LifecycleAction[]>(
    [],
  );
  const [reservations, setReservations] = useState<QuotaReservation[]>([]);
  const [scorecards, setScorecards] = useState<EntityScorecardResult[]>([]);
  const [driftObservations, setDriftObservations] = useState<
    DriftObservation[]
  >([]);
  const [driftReconciliations, setDriftReconciliations] = useState<
    DriftReconciliation[]
  >([]);
  const [entitlements, setEntitlements] = useState<ProjectEntitlement[]>([]);
  const [templateAccess, setTemplateAccess] = useState<TemplateAccessItem[]>(
    [],
  );
  const [boundaryResults, setBoundaryResults] = useState<
    BoundaryValidationResult[]
  >([]);
  const [validatingBoundaries, setValidatingBoundaries] = useState(false);
  const [error, setError] = useState<Error>();

  const callBackend = useCallback(
    async (path: string, init?: RequestInit) => {
      const [baseUrl, credentials] = await Promise.all([
        discoveryApi.getBaseUrl('arta-governance'),
        identityApi.getCredentials(),
      ]);
      const response = await fetch(`${baseUrl}${path}`, {
        ...init,
        headers: {
          ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
          ...(credentials.token
            ? { Authorization: `Bearer ${credentials.token}` }
            : {}),
          ...init?.headers,
        },
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          body.error ?? `Request failed with HTTP ${response.status}`,
        );
      }
      return body;
    },
    [discoveryApi, identityApi],
  );

  const loadRequests = useCallback(async () => {
    const result = await callBackend('/access-requests');
    setRequests(result.items ?? []);
  }, [callBackend]);

  const loadServiceMetadata = useCallback(async () => {
    const result = await callBackend('/service-metadata');
    setServiceMetadata(result.items ?? []);
  }, [callBackend]);

  const loadCostObservations = useCallback(async () => {
    const result = await callBackend('/cost-observations');
    setCostObservations(result.items ?? []);
  }, [callBackend]);

  const loadLifecycleActions = useCallback(async () => {
    const result = await callBackend('/lifecycle-actions');
    setLifecycleActions(result.items ?? []);
  }, [callBackend]);

  const loadReservations = useCallback(async () => {
    const result = await callBackend('/reservations');
    setReservations(result.items ?? []);
  }, [callBackend]);

  const loadDriftReconciliations = useCallback(async () => {
    const result = await callBackend('/drift-reconciliations');
    setDriftReconciliations(result.items ?? []);
  }, [callBackend]);

  const loadBoundaryAccess = useCallback(async () => {
    const [entitlementResponse, accessResponse] = await Promise.all([
      callBackend('/entitlements'),
      callBackend('/template-access'),
    ]);
    setEntitlements(entitlementResponse.items ?? []);
    setTemplateAccess(accessResponse.items ?? []);
  }, [callBackend]);

  useEffect(() => {
    Promise.all([catalogApi.getEntities(), identityApi.getBackstageIdentity()])
      .then(([catalog, identity]) => {
        const ownershipRefs = new Set(
          identity.ownershipEntityRefs.map(ref => normalizeRef(ref)),
        );
        setEntities(
          catalog.items.filter(
            entity =>
              !['Template', 'User', 'Group'].includes(entity.kind) &&
              isOwned(entity, ownershipRefs),
          ),
        );
      })
      .catch(cause =>
        setError(cause instanceof Error ? cause : new Error(String(cause))),
      );
  }, [catalogApi, identityApi]);

  useEffect(() => {
    if (!entities) return;
    Promise.allSettled(
      entities.map(entity => {
        const entityRef = `${entity.kind}:${
          entity.metadata.namespace ?? 'default'
        }/${entity.metadata.name}`.toLowerCase();
        return callBackend(
          `/scorecards?entityRef=${encodeURIComponent(entityRef)}`,
        );
      }),
    ).then(results => {
      setScorecards(
        results.flatMap(result =>
          result.status === 'fulfilled' ? result.value.items ?? [] : [],
        ),
      );
    });
    Promise.allSettled(
      entities.map(entity => {
        const entityRef = `${entity.kind}:${
          entity.metadata.namespace ?? 'default'
        }/${entity.metadata.name}`.toLowerCase();
        return callBackend(
          `/drift-observations?entityRef=${encodeURIComponent(entityRef)}`,
        );
      }),
    ).then(results => {
      setDriftObservations(
        results.flatMap(result =>
          result.status === 'fulfilled' ? result.value.items ?? [] : [],
        ),
      );
    });
  }, [callBackend, entities]);

  useEffect(() => {
    Promise.all([
      loadRequests(),
      loadServiceMetadata(),
      loadCostObservations(),
      loadLifecycleActions(),
      loadReservations(),
      loadDriftReconciliations(),
      loadBoundaryAccess(),
    ]).catch(cause =>
      setError(cause instanceof Error ? cause : new Error(String(cause))),
    );
  }, [
    loadCostObservations,
    loadLifecycleActions,
    loadDriftReconciliations,
    loadBoundaryAccess,
    loadRequests,
    loadReservations,
    loadServiceMetadata,
  ]);

  const validateCloudBoundaries = async () => {
    setValidatingBoundaries(true);
    setBoundaryResults([]);
    try {
      const checks: Array<{
        cloud: 'AWS' | 'Azure';
        entitlement: ProjectEntitlement;
        access: TemplateAccessItem;
        approvedTarget: Record<string, string>;
        escapedTarget: Record<string, string>;
      }> = [];
      for (const entitlement of entitlements) {
        const projectAccess = templateAccess.filter(
          item =>
            normalizeRef(item.projectRef, 'component') ===
            normalizeRef(entitlement.projectRef, 'component'),
        );
        const azureAccess = projectAccess.find(item =>
          item.templateRef.toLowerCase().includes('azure'),
        );
        if (entitlement.azure?.subscriptionIds[0] && azureAccess) {
          const subscriptionId = entitlement.azure.subscriptionIds[0];
          checks.push({
            cloud: 'Azure',
            entitlement,
            access: azureAccess,
            approvedTarget: {
              cloud: 'azure',
              subscriptionId,
              ...(entitlement.azure.regions?.[0]
                ? { region: entitlement.azure.regions[0] }
                : {}),
              ...(entitlement.azure.resourceGroups?.[0]
                ? { resourceGroup: entitlement.azure.resourceGroups[0] }
                : {}),
            },
            escapedTarget: {
              cloud: 'azure',
              subscriptionId:
                subscriptionId.toLowerCase() ===
                '00000000-0000-0000-0000-000000000000'
                  ? 'ffffffff-ffff-ffff-ffff-ffffffffffff'
                  : '00000000-0000-0000-0000-000000000000',
              ...(entitlement.azure.regions?.[0]
                ? { region: entitlement.azure.regions[0] }
                : {}),
            },
          });
        }
        const awsAccess = projectAccess.find(item =>
          item.templateRef.toLowerCase().includes('aws'),
        );
        if (entitlement.aws?.accountIds[0] && awsAccess) {
          const accountId = entitlement.aws.accountIds[0];
          checks.push({
            cloud: 'AWS',
            entitlement,
            access: awsAccess,
            approvedTarget: {
              cloud: 'aws',
              accountId,
              ...(entitlement.aws.regions?.[0]
                ? { region: entitlement.aws.regions[0] }
                : {}),
            },
            escapedTarget: {
              cloud: 'aws',
              accountId:
                accountId === '000000000000' ? '999999999999' : '000000000000',
              ...(entitlement.aws.regions?.[0]
                ? { region: entitlement.aws.regions[0] }
                : {}),
            },
          });
        }
      }

      const results = await Promise.all(
        checks.map(async check => {
          const decide = (target: Record<string, string>) =>
            callBackend('/template-decisions', {
              method: 'POST',
              body: JSON.stringify({
                projectRef: check.entitlement.projectRef,
                templateRef: check.access.templateRef,
                operation: check.access.operation,
                target,
              }),
            });
          const [approved, escaped] = await Promise.all([
            decide(check.approvedTarget),
            decide(check.escapedTarget),
          ]);
          return {
            cloud: check.cloud,
            projectRef: check.entitlement.projectRef,
            templateRef: check.access.templateRef,
            approvedTarget: JSON.stringify(check.approvedTarget),
            approvedResult: approved.allowed
              ? 'Allowed'
              : `Denied: ${approved.reason}`,
            escapedTarget: JSON.stringify(check.escapedTarget),
            escapedResult: escaped.allowed
              ? 'Allowed (unexpected)'
              : `Denied: ${escaped.reason}`,
            passed: Boolean(approved.allowed && !escaped.allowed),
          } as BoundaryValidationResult;
        }),
      );
      setBoundaryResults(results);
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setValidatingBoundaries(false);
    }
  };

  const latestDrift = useMemo(() => {
    const latest = new Map<string, DriftObservation>();
    for (const observation of driftObservations) {
      const current = latest.get(observation.entityRef);
      if (
        !current ||
        Date.parse(observation.observedAt) > Date.parse(current.observedAt)
      ) {
        latest.set(observation.entityRef, observation);
      }
    }
    return [...latest.values()];
  }, [driftObservations]);

  const requestReconciliation = async (observation: DriftObservation) => {
    const reason = window.prompt(
      `Why should ARTA restore the reviewed intent for ${observation.entityRef}?`,
    );
    if (!reason?.trim()) return;
    try {
      await callBackend('/drift-reconciliations', {
        method: 'POST',
        body: JSON.stringify({
          observationId: observation.id,
          strategy: 'apply-intent',
          reason: reason.trim(),
        }),
      });
      await loadDriftReconciliations();
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    }
  };

  const rows = useMemo(() => {
    const snapshots = new Map(
      serviceMetadata.map(snapshot => [snapshot.entityRef, snapshot]),
    );
    const latestCostByEntity = new Map<string, MonthlyCostObservation>();
    for (const observation of costObservations) {
      if (!observation.entityRef) continue;
      const current = latestCostByEntity.get(observation.entityRef);
      if (
        !current ||
        Date.parse(observation.observedAt) > Date.parse(current.observedAt)
      ) {
        latestCostByEntity.set(observation.entityRef, observation);
      }
    }
    return (entities ?? [])
      .map(toRow)
      .map(row => withSynchronizedMetadata(row, snapshots.get(row.entityRef)))
      .map(row => {
        const cost = latestCostByEntity.get(row.entityRef);
        return {
          ...row,
          actualMonthlyCost: cost
            ? `${cost.currency} ${cost.amount.toFixed(2)}`
            : '',
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [costObservations, entities, serviceMetadata]);

  const createRequest = async (
    row: AccessRow,
    requestType: 'access' | 'support',
  ) => {
    const reason = window.prompt(
      requestType === 'access'
        ? `Why do you need access to ${row.name}?`
        : `What support do you need for ${row.name}?`,
    );
    if (!reason?.trim()) return;
    try {
      await callBackend('/access-requests', {
        method: 'POST',
        body: JSON.stringify({
          entityRef: row.entityRef,
          requestType,
          reason: reason.trim(),
        }),
      });
      await loadRequests();
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    }
  };

  const updateRequest = async (
    item: VisibleServiceAccessRequest,
    status: 'approved' | 'denied' | 'completed' | 'cancelled',
  ) => {
    const resolution = window.prompt(
      status === 'cancelled'
        ? 'Why are you cancelling this request?'
        : `Resolution note for ${status}`,
    );
    if (!resolution?.trim()) return;
    try {
      await callBackend(`/access-requests/${item.id}/status`, {
        method: 'POST',
        body: JSON.stringify({ status, resolution: resolution.trim() }),
      });
      await loadRequests();
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    }
  };

  return (
    <Page themeId="home">
      <Header
        title="My Service Access"
        subtitle="Find approved services and continue work in their native tools"
      />
      <Content>
        {error && <ResponseErrorPanel error={error} />}
        {!entities && !error && <Progress />}
        {entities && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <InfoCard title="Your approved service destinations">
                <Typography>
                  ARTA shows catalog entities owned by your identity groups. A
                  link helps you reach the service; access inside that service
                  still follows its native identity and network controls.
                </Typography>
              </InfoCard>
            </Grid>
            <Grid item xs={12}>
              <InfoCard title="Cloud boundary validation">
                <Typography paragraph>
                  Safely evaluate the current identity against its approved
                  cloud target and a synthetic out-of-scope target. This records
                  authorization decisions without assuming a role, opening a
                  service connection, or provisioning resources.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={
                    validatingBoundaries ||
                    entitlements.length === 0 ||
                    templateAccess.length === 0
                  }
                  onClick={validateCloudBoundaries}
                >
                  {validatingBoundaries
                    ? 'Validating…'
                    : 'Validate my cloud boundaries'}
                </Button>
                {entitlements.length === 0 && (
                  <Typography variant="body2" color="textSecondary">
                    No active project entitlement is assigned to this identity.
                  </Typography>
                )}
              </InfoCard>
            </Grid>
            {boundaryResults.length > 0 && (
              <Grid item xs={12}>
                <Table
                  title={`Boundary results (${boundaryResults.length})`}
                  options={{ paging: false, search: false }}
                  columns={[
                    { title: 'Cloud', field: 'cloud' },
                    { title: 'Project', field: 'projectRef' },
                    { title: 'Template', field: 'templateRef' },
                    { title: 'Approved target', field: 'approvedTarget' },
                    { title: 'Approved decision', field: 'approvedResult' },
                    { title: 'Out-of-scope target', field: 'escapedTarget' },
                    { title: 'Boundary decision', field: 'escapedResult' },
                    {
                      title: 'Result',
                      render: row => (row.passed ? 'PASS' : 'FAIL'),
                    },
                  ]}
                  data={boundaryResults}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <Table
                title={`Services (${rows.length})`}
                options={{ paging: true, pageSize: 20, search: true }}
                columns={[
                  {
                    title: 'Service',
                    render: row => (
                      <a href={catalogPath(row.entityRef)}>{row.name}</a>
                    ),
                  },
                  { title: 'Kind', field: 'kind' },
                  { title: 'Type', field: 'type' },
                  { title: 'Cloud', field: 'cloud' },
                  { title: 'Environment', field: 'environment' },
                  { title: 'Status', field: 'serviceStatus' },
                  { title: 'Metadata', field: 'metadataFreshness' },
                  { title: 'Month-to-date cost', field: 'actualMonthlyCost' },
                  { title: 'Connection', field: 'connection' },
                  {
                    title: 'Destinations',
                    render: row =>
                      row.destinations.length ? (
                        <>
                          {row.destinations.map((destination, index) => (
                            <React.Fragment key={destination.url}>
                              {index > 0 && ' · '}
                              <a
                                href={destination.url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {destination.title}
                              </a>
                            </React.Fragment>
                          ))}
                        </>
                      ) : (
                        'Open catalog entry'
                      ),
                  },
                  {
                    title: 'Access and support',
                    render: row => (
                      <>
                        <Typography variant="body2">
                          {row.accessInstructions ||
                            (row.owner
                              ? `Contact ${row.owner}`
                              : 'See catalog owner')}
                        </Typography>
                        <Button
                          size="small"
                          color="primary"
                          onClick={() => createRequest(row, 'access')}
                        >
                          Request access
                        </Button>
                        <Button
                          size="small"
                          onClick={() => createRequest(row, 'support')}
                        >
                          Request support
                        </Button>
                      </>
                    ),
                  },
                ]}
                data={rows}
              />
            </Grid>
            <Grid item xs={12}>
              <Table
                title={`Platform assurance (${scorecards.length})`}
                options={{ paging: true, pageSize: 10, search: true }}
                columns={[
                  { title: 'Service', field: 'entityRef' },
                  {
                    title: 'Scorecard',
                    render: item =>
                      `${item.scorecardId}@${item.scorecardVersion}`,
                  },
                  { title: 'Status', field: 'status' },
                  { title: 'Score', render: item => `${item.score}%` },
                  {
                    title: 'Passing checks',
                    render: item =>
                      `${item.passedChecks}/${item.evaluatedChecks}`,
                  },
                  {
                    title: 'Evidence gaps',
                    render: item =>
                      item.checks
                        .filter(
                          check =>
                            check.status !== 'pass' &&
                            check.status !== 'not-applicable',
                        )
                        .map(check => `${check.check.title}: ${check.status}`)
                        .join(' · '),
                  },
                ]}
                data={scorecards}
              />
            </Grid>
            <Grid item xs={12}>
              <Table
                title={`Infrastructure drift (${latestDrift.length})`}
                options={{ paging: true, pageSize: 10, search: true }}
                columns={[
                  { title: 'Service', field: 'entityRef' },
                  { title: 'Cloud', field: 'provider' },
                  { title: 'Status', field: 'status' },
                  {
                    title: 'Differences',
                    render: item =>
                      item.differences
                        .map(change => `${change.path} (${change.severity})`)
                        .join(' · '),
                  },
                  {
                    title: 'Observed',
                    render: item => new Date(item.observedAt).toLocaleString(),
                  },
                  {
                    title: 'Reconciliation',
                    render: item => {
                      const open = driftReconciliations.find(
                        action =>
                          action.observationId === item.id &&
                          ['requested', 'approved', 'executing'].includes(
                            action.status,
                          ),
                      );
                      if (open) return open.status;
                      return item.status === 'drifted' ? (
                        <Button
                          size="small"
                          color="primary"
                          onClick={() => requestReconciliation(item)}
                        >
                          Request restore
                        </Button>
                      ) : (
                        ''
                      );
                    },
                  },
                ]}
                data={latestDrift}
              />
            </Grid>
            <Grid item xs={12}>
              <Table
                title={`Access and support requests (${requests.length})`}
                options={{ paging: true, pageSize: 10, search: true }}
                columns={[
                  { title: 'Service', field: 'entityRef' },
                  { title: 'Type', field: 'requestType' },
                  { title: 'Reason', field: 'reason' },
                  { title: 'Requested by', field: 'requestedBy' },
                  { title: 'Owner', field: 'ownerRef' },
                  { title: 'Status', field: 'status' },
                  { title: 'Resolution', field: 'resolution' },
                  {
                    title: 'Actions',
                    render: item => {
                      if (item.canCancel) {
                        return (
                          <Button
                            size="small"
                            onClick={() => updateRequest(item, 'cancelled')}
                          >
                            Cancel
                          </Button>
                        );
                      }
                      if (item.status === 'requested' && item.canResolve) {
                        return (
                          <>
                            <Button
                              size="small"
                              color="primary"
                              onClick={() => updateRequest(item, 'approved')}
                            >
                              Approve
                            </Button>
                            <Button
                              size="small"
                              onClick={() => updateRequest(item, 'denied')}
                            >
                              Deny
                            </Button>
                          </>
                        );
                      }
                      if (item.status === 'approved' && item.canResolve) {
                        return (
                          <Button
                            size="small"
                            onClick={() => updateRequest(item, 'completed')}
                          >
                            Complete
                          </Button>
                        );
                      }
                      return '';
                    },
                  },
                ]}
                data={requests}
              />
            </Grid>
            <Grid item xs={12}>
              <Table
                title={`Ephemeral environments (${
                  reservations.filter(
                    item => item.environmentClass === 'ephemeral',
                  ).length
                })`}
                options={{ paging: true, pageSize: 10, search: true }}
                columns={[
                  { title: 'Environment', field: 'resourceKey' },
                  { title: 'Project', field: 'projectRef' },
                  { title: 'Trigger', render: item => item.ephemeral?.trigger },
                  {
                    title: 'Pull request',
                    render: item =>
                      item.ephemeral?.pullRequestRef ? (
                        <a
                          href={item.ephemeral.pullRequestRef}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Open
                        </a>
                      ) : (
                        ''
                      ),
                  },
                  {
                    title: 'Data seed',
                    render: item => item.ephemeral?.dataSeedRef ?? '',
                  },
                  {
                    title: 'Expires',
                    render: item =>
                      item.leaseExpiresAt
                        ? new Date(item.leaseExpiresAt).toLocaleString()
                        : '',
                  },
                  {
                    title: 'Lifetime estimate',
                    render: item =>
                      item.estimatedLifetimeCost === undefined
                        ? ''
                        : `USD ${item.estimatedLifetimeCost.toFixed(2)}`,
                  },
                  {
                    title: 'Status',
                    render: item =>
                      item.cleanupRequired
                        ? `${item.status} · cleanup required`
                        : item.status,
                  },
                ]}
                data={reservations.filter(
                  item => item.environmentClass === 'ephemeral',
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Table
                title={`Scheduled lifecycle actions (${lifecycleActions.length})`}
                options={{ paging: true, pageSize: 10, search: true }}
                columns={[
                  { title: 'Service', field: 'entityRef' },
                  { title: 'Project', field: 'projectRef' },
                  { title: 'Cloud', field: 'cloud' },
                  { title: 'Action', field: 'action' },
                  { title: 'Trigger', field: 'trigger' },
                  { title: 'Reason', field: 'reason' },
                  { title: 'Status', field: 'status' },
                  {
                    title: 'Scheduled',
                    render: item =>
                      new Date(item.scheduledFor).toLocaleString(),
                  },
                  { title: 'Result', field: 'resultMessage' },
                ]}
                data={lifecycleActions}
              />
            </Grid>
          </Grid>
        )}
      </Content>
    </Page>
  );
};
